import React, { useState } from 'react';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import { DATE_TYPE_DATE, DATE_TYPE_TIME } from '../../../../../util/types';

// Import shared components
import { Heading, IconDelete, IconSpinner, TimeRange } from '../../../../../components';

import css from './AvailableDatesList.module.css';

// Group exceptions by "year-month" so we can render clear month headings.
const groupByMonth = (exceptions, intl, timeZone) => {
  return exceptions.reduce((groups, exception) => {
    const start = exception.attributes.start;
    const key = intl.formatDate(start, { year: 'numeric', month: 'numeric', timeZone });
    const label = intl.formatDate(start, { year: 'numeric', month: 'long', timeZone });
    const group = groups[key] || { label, items: [] };
    group.items.push(exception);
    return { ...groups, [key]: group };
  }, {});
};

// A single available date (exception) row.
const AvailableDateItem = ({
  exception,
  timeZone,
  isDaily,
  useFullDays,
  useMultipleSeats,
  onDeleteAvailabilityException,
  isDeleting,
}) => {
  const { start, end, seats } = exception.attributes;

  // For daily bookings, the exclusive end is the start of the next day, so we
  // step one millisecond back to display the inclusive last available day.
  const displayEnd = isDaily && useFullDays ? new Date(end.getTime() - 1) : end;

  return (
    <li className={css.item}>
      <span className={css.availableDot} />
      <div className={css.itemInfo}>
        <TimeRange
          className={css.itemRange}
          startDate={start}
          endDate={displayEnd}
          dateType={useFullDays ? DATE_TYPE_DATE : DATE_TYPE_TIME}
          timeZone={timeZone}
        />
        {useMultipleSeats && seats > 0 ? (
          <span className={css.itemSeats}>
            <FormattedMessage
              id="EditListingAvailabilityPanel.AvailableDatesList.seats"
              values={{ seats }}
            />
          </span>
        ) : null}
      </div>

      <button
        type="button"
        className={css.deleteButton}
        disabled={isDeleting}
        onClick={() => onDeleteAvailabilityException({ id: exception.id })}
      >
        {isDeleting ? (
          <IconSpinner className={css.deleteSpinner} />
        ) : (
          <span className={css.deleteIconWrapper}>
            <IconDelete rootClassName={css.deleteIcon} />
          </span>
        )}
      </button>
    </li>
  );
};

/**
 * A clean list of the dates a provider has marked available (availability
 * exceptions with seats > 0). This replaces the weekly grid that also rendered
 * the empty default schedule, which was confusing for providers.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.headerClassName
 * @param {Array<Object>} props.availabilityExceptions
 * @param {string} props.timeZone IANA time zone key
 * @param {boolean} props.isDaily
 * @param {boolean} props.useFullDays
 * @param {boolean} props.useMultipleSeats
 * @param {boolean} props.fetchInProgress
 * @param {Object|null} props.fetchError
 * @param {Function} props.onDeleteAvailabilityException
 * @returns {JSX.Element}
 */
const AvailableDatesList = props => {
  const intl = useIntl();
  const [deletingId, setDeletingId] = useState(null);

  const {
    className,
    headerClassName,
    availabilityExceptions = [],
    timeZone,
    isDaily,
    useFullDays,
    useMultipleSeats,
    fetchInProgress,
    fetchError,
    onDeleteAvailabilityException,
  } = props;

  const now = new Date();

  // Only show dates that are actually available (seats > 0) and haven't fully
  // passed yet. Sort them chronologically for easy scanning.
  const availableDates = availabilityExceptions
    .filter(e => e?.attributes?.seats > 0 && e.attributes.end > now)
    .sort((a, b) => a.attributes.start - b.attributes.start);

  const handleDelete = params => {
    setDeletingId(params.id?.uuid);
    return Promise.resolve(onDeleteAvailabilityException(params))
      .catch(() => {})
      .finally(() => setDeletingId(null));
  };

  const groups = groupByMonth(availableDates, intl, timeZone);
  const groupKeys = Object.keys(groups);

  const classes = classNames(css.root, className);

  return (
    <section className={classes}>
      <header className={classNames(css.header, headerClassName)}>
        <Heading as="h2" className={css.sectionTitle}>
          <FormattedMessage id="EditListingAvailabilityPanel.AvailableDatesList.title" />
        </Heading>
        {availableDates.length > 0 ? (
          <span className={css.count}>
            <FormattedMessage
              id="EditListingAvailabilityPanel.AvailableDatesList.count"
              values={{ count: availableDates.length }}
            />
          </span>
        ) : null}
      </header>

      {fetchError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingAvailabilityPanel.AvailableDatesList.fetchError" />
        </p>
      ) : null}

      {availableDates.length === 0 ? (
        fetchInProgress ? (
          <div className={css.loading}>
            <IconSpinner />
          </div>
        ) : (
          <div className={css.emptyState}>
            <p className={css.emptyTitle}>
              <FormattedMessage id="EditListingAvailabilityPanel.AvailableDatesList.emptyTitle" />
            </p>
            <p className={css.emptyText}>
              <FormattedMessage id="EditListingAvailabilityPanel.AvailableDatesList.emptyText" />
            </p>
          </div>
        )
      ) : (
        <div className={css.groups}>
          {groupKeys.map(key => {
            const group = groups[key];
            return (
              <div key={key} className={css.group}>
                <h3 className={css.groupLabel}>{group.label}</h3>
                <ul className={css.list}>
                  {group.items.map(exception => (
                    <AvailableDateItem
                      key={exception.id.uuid}
                      exception={exception}
                      timeZone={timeZone}
                      isDaily={isDaily}
                      useFullDays={useFullDays}
                      useMultipleSeats={useMultipleSeats}
                      onDeleteAvailabilityException={handleDelete}
                      isDeleting={deletingId === exception.id.uuid}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AvailableDatesList;
